import React, { useEffect, useState, useMemo } from 'react';
import { 
    Card, 
    CardContent, 
    Typography, 
    Box, 
    Skeleton, 
    ToggleButtonGroup, 
    ToggleButton, 
    Chip,
    Tooltip
} from '@mui/material';
import BarChartIcon from '@mui/icons-material/BarChart';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import { FinanceService } from '../../../api/services/financeService';
import { formatCurrency, useCurrency } from '../../../utils/currency';

export default function FinancialTrendsChart() {
    const { currency } = useCurrency();
    const [chartData, setChartData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [hoveredIdx, setHoveredIdx] = useState(null);
    const [chartType, setChartType] = useState('bars'); // 'bars' | 'area'

    const fetchChartData = async () => {
        setLoading(true);
        try {
            const res = await FinanceService.getFinancialTrends();
            // Perfectly handle { status: "success", data: [ ... ] }, [ ... ], or { trends: [ ... ] }
            const raw = res?.data || res;
            const dataArray = Array.isArray(raw) 
                ? raw 
                : (Array.isArray(raw?.trends) ? raw.trends : (Array.isArray(res?.trends) ? res.trends : null));
            if (dataArray && dataArray.length > 0) {
                setChartData(dataArray);
            } else {
                throw new Error("Invalid response format");
            }
        } catch (err) {
            console.error('Failed to fetch financial trends data:', err);
            setChartData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchChartData();
    }, []);

    // Aggregate summary statistics
    const totals = useMemo(() => {
        if (!chartData || chartData.length === 0) return { revenue: 0, expenses: 0, profit: 0 };
        let rev = 0;
        let exp = 0;

        chartData.forEach((d) => {
            rev += Number(d.revenue ?? d.rev ?? 0);
            exp += Number(d.expenses ?? d.exp ?? 0);
        });

        const profit = rev - exp;
        return { revenue: rev, expenses: exp, profit };
    }, [chartData]);

    if (loading) {
        return (
            <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', height: '100%', bgcolor: '#ffffff' }}>
                <CardContent sx={{ p: 3.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                        <Box>
                            <Skeleton variant="text" width={240} height={36} />
                            <Skeleton variant="text" width={180} height={20} />
                        </Box>
                        <Skeleton variant="rectangular" width={120} height={36} sx={{ borderRadius: 2 }} />
                    </Box>
                    <Box sx={{ height: 260, display: 'flex', alignItems: 'flex-end', gap: 3, px: 2, pt: 4 }}>
                        {[...Array(6)].map((_, i) => (
                            <Skeleton 
                                key={i} 
                                variant="rectangular" 
                                sx={{ 
                                    flex: 1, 
                                    height: `${30 + Math.random() * 60}%`, 
                                    borderRadius: '8px 8px 0 0' 
                                }} 
                            />
                        ))}
                    </Box>
                </CardContent>
            </Card>
        );
    }

    if (!chartData || chartData.length === 0) return null;

    const maxVal = Math.max(...chartData.map(d => Math.max(Number(d.revenue ?? d.rev ?? 0), Number(d.expenses ?? d.exp ?? 0))), 1000);
    const N = chartData.length;
    
    const svgWidth = 1100;
    const svgHeight = 350;
    const paddingLeft = 110;
    const paddingRight = 40;
    const paddingTop = 35;
    const paddingBottom = 45;
    
    const plotWidth = svgWidth - paddingLeft - paddingRight;
    const plotHeight = svgHeight - paddingTop - paddingBottom;

    const getY = (val) => {
        if (maxVal <= 0) return paddingTop + plotHeight;
        return paddingTop + plotHeight - (val / maxVal) * plotHeight;
    };

    const gridLevels = [0, 25, 50, 75, 100];

    const points = chartData.map((item, idx) => {
        const label = (item.month || item.label || `M${idx + 1}`).toUpperCase();
        const revenue = Number(item.revenue ?? item.rev ?? 0);
        const expenses = Number(item.expenses ?? item.exp ?? 0);
        const profit = revenue - expenses;
        const margin = revenue > 0 ? ((profit / revenue) * 100).toFixed(0) : '0';
        
        const xCenter = paddingLeft + (idx * (plotWidth / N)) + (plotWidth / N / 2);
        
        return {
            xCenter,
            yRev: getY(revenue),
            yExp: getY(expenses),
            label,
            revenue,
            expenses,
            profit,
            margin
        };
    });

    const colWidth = plotWidth / N;
    // Modern balanced bar sizing with comfortable spacing
    const barWidth = Math.min(38, Math.max(22, (colWidth - 36) / 2));
    const barGap = 6;

    // Generate smooth SVG path for Area curve
    const buildPath = (key) => {
        if (points.length === 0) return '';
        let d = `M ${points[0].xCenter} ${points[0][key]}`;
        for (let i = 0; i < points.length - 1; i++) {
            const curr = points[i];
            const next = points[i + 1];
            const cpX1 = curr.xCenter + (next.xCenter - curr.xCenter) / 2;
            const cpY1 = curr[key];
            const cpX2 = cpX1;
            const cpY2 = next[key];
            d += ` C ${cpX1} ${cpY1}, ${cpX2} ${cpY2}, ${next.xCenter} ${next[key]}`;
        }
        return d;
    };

    const revPath = buildPath('yRev');
    const expPath = buildPath('yExp');
    const revAreaPath = `${revPath} L ${points[points.length - 1].xCenter} ${paddingTop + plotHeight} L ${points[0].xCenter} ${paddingTop + plotHeight} Z`;

    return (
        <Card 
            elevation={0}
            sx={{ 
                borderRadius: '12px', 
                border: '1px solid #eaecf0', 
                bgcolor: '#ffffff',
                boxShadow: 'none',
                overflow: 'hidden'
            }}
        >
            <CardContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
                {/* Header & Controls */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                    <Box>
                        <Typography variant="h6" fontWeight="800" sx={{ color: '#0f172a', letterSpacing: '-0.02em', fontSize: '18px' }}>
                            Financial Trends (Last 6 Months)
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5, fontSize: '13px' }}>
                            Historical revenue performance vs operational expenses.
                        </Typography>
                    </Box>

                    {/* Chart Style Toggle & Legend */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        {/* Legend */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, bgcolor: '#f8fafc', px: 2, py: 0.75, borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ width: 12, height: 12, borderRadius: '4px', background: 'linear-gradient(180deg, #10b981 0%, #059669 100%)', boxShadow: '0 2px 4px rgba(16,185,129,0.3)' }} />
                                <Typography variant="caption" sx={{ color: '#1e293b', fontWeight: 700, fontSize: '12px' }}>
                                    Revenue
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ width: 12, height: 12, borderRadius: '4px', background: 'linear-gradient(180deg, #f43f5e 0%, #e11d48 100%)', boxShadow: '0 2px 4px rgba(244,63,94,0.3)' }} />
                                <Typography variant="caption" sx={{ color: '#1e293b', fontWeight: 700, fontSize: '12px' }}>
                                    Expenses
                                </Typography>
                            </Box>
                        </Box>

                        {/* Chart View Toggle */}
                        <ToggleButtonGroup
                            value={chartType}
                            exclusive
                            onChange={(e, val) => { if (val) setChartType(val); }}
                            size="small"
                            sx={{
                                '& .MuiToggleButton-root': {
                                    px: 1.5,
                                    py: 0.5,
                                    borderRadius: '8px !important',
                                    border: '1px solid #e2e8f0',
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    fontSize: '12px',
                                    color: '#64748b',
                                    '&.Mui-selected': {
                                        bgcolor: '#0f172a',
                                        color: '#ffffff',
                                        '&:hover': { bgcolor: '#1e293b' }
                                    }
                                }
                            }}
                        >
                            <ToggleButton value="bars">
                                <BarChartIcon sx={{ fontSize: 18, mr: 0.5 }} /> Bars
                            </ToggleButton>
                            <ToggleButton value="area">
                                <ShowChartIcon sx={{ fontSize: 18, mr: 0.5 }} /> Curve
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                </Box>

                {/* SVG High-Definition Chart */}
                <Box sx={{ width: '100%', position: 'relative', overflowX: 'auto' }}>
                    <svg 
                        viewBox={`0 0 ${svgWidth} ${svgHeight}`} 
                        width="100%" 
                        height="auto" 
                        style={{ overflow: 'visible', minWidth: '700px', display: 'block' }}
                    >
                        <defs>
                            {/* Revenue Bar Gradient */}
                            <linearGradient id="revBarGrad" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="#10b981" />
                                <stop offset="100%" stopColor="#059669" />
                            </linearGradient>

                            {/* Revenue Bar Hover Gradient */}
                            <linearGradient id="revBarHoverGrad" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="#34d399" />
                                <stop offset="100%" stopColor="#059669" />
                            </linearGradient>

                            {/* Expenses Bar Gradient */}
                            <linearGradient id="expBarGrad" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="#f43f5e" />
                                <stop offset="100%" stopColor="#be123c" />
                            </linearGradient>

                            {/* Area Gradient for Curve mode */}
                            <linearGradient id="areaRevGrad" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="#10b981" stopOpacity="0.35" />
                                <stop offset="100%" stopColor="#10b981" stopOpacity="0.0" />
                            </linearGradient>

                            {/* Soft shadow for peak month badge */}
                            <filter id="softGlow" x="-20%" y="-20%" width="140%" height="140%">
                                <feDropShadow dx="0" dy="4" stdDeviation="4" floodColor="#0f172a" floodOpacity="0.1" />
                            </filter>
                        </defs>

                        {/* Background Alternating Grid lines & Y-axes labels */}
                        {gridLevels.map(level => {
                            const val = (level / 100) * maxVal;
                            const y = getY(val);
                            return (
                                <g key={`grid-${level}`}>
                                    <line 
                                        x1={paddingLeft} 
                                        y1={y} 
                                        x2={paddingLeft + plotWidth} 
                                        y2={y} 
                                        stroke={level === 0 ? '#cbd5e1' : '#f1f5f9'} 
                                        strokeWidth={level === 0 ? 1.5 : 1}
                                        strokeDasharray={level === 0 ? 'none' : '4 4'} 
                                    />
                                    <text 
                                        x={paddingLeft - 18} 
                                        y={y + 4} 
                                        textAnchor="end" 
                                        fontSize={12} 
                                        fill="#64748b"
                                        fontWeight="600"
                                        fontFamily="-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
                                    >
                                        {formatCurrency(Math.round(val), currency).replace(/\.00$/, '')}
                                    </text>
                                </g>
                            );
                        })}

                        {/* Hover Column Background Highlight */}
                        {hoveredIdx !== null && points[hoveredIdx] && (
                            <rect 
                                x={points[hoveredIdx].xCenter - colWidth / 2 + 8}
                                y={paddingTop}
                                width={colWidth - 16}
                                height={plotHeight}
                                rx={10}
                                fill="rgba(15, 23, 42, 0.03)"
                                style={{ transition: 'all 0.2s ease' }}
                            />
                        )}

                        {/* Chart Render: Mode 1: Dual Rounded Capsule Bars */}
                        {chartType === 'bars' && (
                            <g>
                                {points.map((p, idx) => {
                                    const isHovered = hoveredIdx === idx;
                                    
                                    // Visual guard: give tiny non-zero expenses / revenues a minimum visible 8px height
                                    const rawRevHeight = paddingTop + plotHeight - p.yRev;
                                    const revHeight = p.revenue > 0 ? Math.max(8, rawRevHeight) : 0;
                                    const actualYRev = paddingTop + plotHeight - revHeight;
                                    
                                    const rawExpHeight = paddingTop + plotHeight - p.yExp;
                                    const expHeight = p.expenses > 0 ? Math.max(8, rawExpHeight) : 0;
                                    const actualYExp = paddingTop + plotHeight - expHeight;

                                    return (
                                        <g key={`bars-${idx}`} style={{ cursor: 'pointer' }}>
                                            {/* Revenue Bar */}
                                            {revHeight > 0 && (
                                                <rect 
                                                    x={p.xCenter - barWidth - barGap/2}
                                                    y={actualYRev}
                                                    width={barWidth}
                                                    height={revHeight}
                                                    rx={Math.min(7, barWidth / 2)}
                                                    fill={isHovered ? 'url(#revBarHoverGrad)' : 'url(#revBarGrad)'}
                                                    opacity={hoveredIdx !== null && !isHovered ? 0.65 : 1}
                                                    style={{ transition: 'all 0.2s ease' }}
                                                />
                                            )}

                                            {/* Expenses Bar */}
                                            {expHeight > 0 && (
                                                <rect 
                                                    x={p.xCenter + barGap/2}
                                                    y={actualYExp}
                                                    width={barWidth}
                                                    height={expHeight}
                                                    rx={Math.min(7, barWidth / 2)}
                                                    fill="url(#expBarGrad)"
                                                    opacity={hoveredIdx !== null && !isHovered ? 0.65 : 1}
                                                    style={{ transition: 'all 0.2s ease' }}
                                                />
                                            )}

                                            {/* Zero value indicators */}
                                            {revHeight === 0 && expHeight === 0 && (
                                                <rect 
                                                    x={p.xCenter - 14}
                                                    y={paddingTop + plotHeight - 3}
                                                    width={28}
                                                    height={3}
                                                    rx={1.5}
                                                    fill="#cbd5e1"
                                                    opacity={isHovered ? 1 : 0.6}
                                                />
                                            )}
                                            {revHeight > 0 && expHeight === 0 && (
                                                <rect 
                                                    x={p.xCenter + barGap/2}
                                                    y={paddingTop + plotHeight - 2}
                                                    width={barWidth}
                                                    height={2}
                                                    rx={1}
                                                    fill="#e2e8f0"
                                                />
                                            )}
                                        </g>
                                    );
                                })}
                            </g>
                        )}

                        {/* Chart Render: Mode 2: Smooth Spline Area Curve */}
                        {chartType === 'area' && (
                            <g>
                                {/* Shaded Area under Revenue Curve */}
                                <path d={revAreaPath} fill="url(#areaRevGrad)" />

                                {/* Revenue Trend Line */}
                                <path 
                                    d={revPath} 
                                    fill="none" 
                                    stroke="#10b981" 
                                    strokeWidth="3.5" 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round" 
                                />

                                {/* Expenses Trend Line */}
                                <path 
                                    d={expPath} 
                                    fill="none" 
                                    stroke="#f43f5e" 
                                    strokeWidth="2.5" 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round" 
                                    strokeDasharray="5 5"
                                />

                                {/* Dots along points */}
                                {points.map((p, idx) => (
                                    <g key={`dots-${idx}`}>
                                        <circle 
                                            cx={p.xCenter} 
                                            cy={p.yRev} 
                                            r={hoveredIdx === idx ? 6 : 4} 
                                            fill="#10b981" 
                                            stroke="#ffffff" 
                                            strokeWidth="2.5" 
                                        />
                                        <circle 
                                            cx={p.xCenter} 
                                            cy={p.yExp} 
                                            r={hoveredIdx === idx ? 5 : 3.5} 
                                            fill="#f43f5e" 
                                            stroke="#ffffff" 
                                            strokeWidth="2" 
                                        />
                                    </g>
                                ))}
                            </g>
                        )}

                        {/* X-Axis Month Labels */}
                        {points.map((p, idx) => {
                            const isHovered = hoveredIdx === idx;
                            return (
                                <g key={`label-${idx}`}>
                                    <text 
                                        x={p.xCenter}
                                        y={paddingTop + plotHeight + 26}
                                        textAnchor="middle"
                                        fontSize={12.5}
                                        fill={isHovered ? '#0f172a' : '#64748b'}
                                        fontWeight={isHovered ? 800 : 700}
                                        fontFamily="-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
                                        style={{ transition: 'all 0.2s ease' }}
                                    >
                                        {p.label}
                                    </text>
                                </g>
                            );
                        })}

                        {/* Invisible Column Hotspots for Precision Hover */}
                        {points.map((p, idx) => (
                            <rect 
                                key={`hotspot-${idx}`}
                                x={p.xCenter - colWidth / 2}
                                y={paddingTop}
                                width={colWidth}
                                height={plotHeight + 35}
                                fill="transparent"
                                style={{ cursor: 'pointer' }}
                                onMouseEnter={() => setHoveredIdx(idx)}
                                onMouseLeave={() => setHoveredIdx(null)}
                            />
                        ))}

                        {/* Floating Tooltip Card */}
                        {hoveredIdx !== null && points[hoveredIdx] && (
                            <g style={{ pointerEvents: 'none' }}>
                                {/* Vertical guideline */}
                                <line 
                                    x1={points[hoveredIdx].xCenter} 
                                    y1={paddingTop} 
                                    x2={points[hoveredIdx].xCenter} 
                                    y2={paddingTop + plotHeight} 
                                    stroke="#94a3b8" 
                                    strokeWidth="1" 
                                    strokeDasharray="3 3" 
                                />

                                <foreignObject
                                    x={
                                        points[hoveredIdx].xCenter + 12 + 165 > svgWidth - paddingRight 
                                            ? points[hoveredIdx].xCenter - 177 
                                            : points[hoveredIdx].xCenter + 12
                                    }
                                    y={Math.min(Math.max(paddingTop + 5, points[hoveredIdx].yRev - 25), paddingTop + plotHeight - 85)}
                                    width={165}
                                    height={85}
                                >
                                    <div style={{
                                        backgroundColor: 'rgba(15, 23, 42, 0.95)',
                                        color: '#ffffff',
                                        padding: '7px 10px',
                                        borderRadius: '8px',
                                        boxShadow: '0 4px 14px rgba(0, 0, 0, 0.25)',
                                        border: '1px solid rgba(255, 255, 255, 0.1)',
                                        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                                        backdropFilter: 'blur(4px)'
                                    }}>
                                        <div style={{ fontWeight: 800, fontSize: '11px', color: '#f8fafc', marginBottom: '4px', borderBottom: '1px solid rgba(255, 255, 255, 0.1)', paddingBottom: '3px' }}>
                                            {points[hoveredIdx].label}
                                        </div>

                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '3px', fontSize: '10.5px' }}>
                                            <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#94a3b8' }}>
                                                <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#10b981', display: 'inline-block' }} />
                                                Rev:
                                            </span>
                                            <span style={{ fontWeight: 700, color: '#34d399' }}>
                                                {formatCurrency(points[hoveredIdx].revenue, currency)}
                                            </span>
                                        </div>

                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '10.5px' }}>
                                            <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#94a3b8' }}>
                                                <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#f43f5e', display: 'inline-block' }} />
                                                Exp:
                                            </span>
                                            <span style={{ fontWeight: 700, color: '#fb7185' }}>
                                                {formatCurrency(points[hoveredIdx].expenses, currency)}
                                            </span>
                                        </div>
                                    </div>
                                </foreignObject>
                            </g>
                        )}
                    </svg>
                </Box>
            </CardContent>
        </Card>
    );
}
